<template>
	<div class="team-fans-overview" v-loading="loading">
		<el-card class="fans-summary-card" shadow="never">
			<div class="card-title-row">
				<div><h3>粉丝概览</h3><span>团队成员增长与活跃情况</span></div>
				<el-button icon="el-icon-share" size="small" plain @click="handleGetShareLink">邀请好友</el-button>
			</div>
			<div class="fans-metrics">
				<div v-for="item in fanMetrics" :key="item.key" class="fan-metric" :class="item.className">
					<div class="metric-label">{{ item.label }}
						<el-tooltip :content="item.tip" placement="top" effect="dark"><i class="el-icon-question help-icon"></i></el-tooltip>
					</div>
					<div class="metric-value">{{ formatNumber(item.value) }}</div>
				</div>
			</div>
		</el-card>

		<div class="charts-grid">
			<el-card class="chart-card distribution-card" shadow="never">
				<div class="card-title-row compact">
					<div><h3>粉丝分布</h3><span>各层级粉丝占比</span></div>
				</div>
				<div class="distribution-content">
					<div ref="levelPieChart" class="pie-chart"></div>
					<div class="level-list">
						<div v-for="item in levelItems" :key="item.key" class="level-item">
							<div class="level-name"><i :style="{ backgroundColor: item.color }"></i>{{ item.label }}
								<el-tooltip :content="item.tip" placement="top"><i class="el-icon-question help-icon"></i></el-tooltip>
							</div>
							<strong>{{ formatNumber(item.total) }}</strong>
							<div v-if="item.key === 'level1'" class="level-extra">
								<span>有效 {{ formatNumber(item.effective) }}
									<el-tooltip content="有过手机号绑定、淘宝备案、下单、提现其中任一行为的用户" placement="top"><i class="el-icon-question help-icon"></i></el-tooltip>
								</span>
								<span>活跃 {{ formatNumber(item.active) }}
									<el-tooltip content="近30天自购或分享产生3笔及以上有效订单的用户" placement="top"><i class="el-icon-question help-icon"></i></el-tooltip>
								</span>
							</div>
						</div>
					</div>
				</div>
			</el-card>

			<el-card class="chart-card trend-card" shadow="never" v-loading="trendLoading">
				<div class="card-title-row compact">
					<div><h3>团队数据趋势</h3><span>粉丝质量变化</span></div>
					<el-radio-group v-model="trendTime" size="mini" @change="fetchTrendData">
						<el-radio-button label="today">今日</el-radio-button>
						<el-radio-button label="yesterday">昨日</el-radio-button>
						<el-radio-button label="seven">近7天</el-radio-button>
						<el-radio-button label="thirty">近30天</el-radio-button>
					</el-radio-group>
				</div>
				<div ref="trendBarChart" class="bar-chart"></div>
			</el-card>
		</div>

		<ShareLinkDialog ref="shareLinkDialog" />
	</div>
</template>

<script>
import * as echarts from 'echarts'
import { getTeamFansIndex, getFansByTime, getAppShareXcode, getAppShareInfo } from '@/api/public'
import ShareLinkDialog from './ShareLinkDialog.vue'

export default {
	name: 'TeamFansOverview',
	components: { ShareLinkDialog },
	data() {
		return {
			loading: false,
			trendLoading: false,
			trendTime: 'today',
			overviewData: {
				fansTot: 0, fansTotToday: 0, fansTotYesterday: 0, fansTotSevenday: 0, fansTotThirtyday: 0,
				level1: { tot: 0, effectiveTot: 0, activeTot: 0 },
				level2: { tot: 0 }, level3: { tot: 0 }
			},
			trendData: { fansTot: 0, effectiveTot: 0, activeTot: 0 },
			pieChart: null,
			barChart: null,
			shareUrl: ''
		}
	},
	computed: {
		userInfo() { return this.$store.getters.info || {} },
		fanMetrics() {
			return [
				{ key: 'total', label: '总粉丝数', value: this.overviewData.fansTot, className: 'is-total', tip: '当前团队内的全部粉丝数量' },
				{ key: 'today', label: '今日新增', value: this.overviewData.fansTotToday, className: 'is-today', tip: '今天新加入团队的粉丝数' },
				{ key: 'yesterday', label: '昨日新增', value: this.overviewData.fansTotYesterday, className: 'is-yesterday', tip: '昨天新加入团队的粉丝数' },
				{ key: 'seven', label: '近7日新增', value: this.overviewData.fansTotSevenday, className: 'is-seven', tip: '最近7天新加入团队的粉丝数' },
				{ key: 'thirty', label: '近30日新增', value: this.overviewData.fansTotThirtyday, className: 'is-thirty', tip: '最近30天新加入团队的粉丝数' }
			]
		},
		levelItems() {
			return [
				{ key: 'level1', label: '直属粉丝', color: '#409eff', total: this.getLevelValue('level1', 'tot'), effective: this.getLevelValue('level1', 'effectiveTot'), active: this.getLevelValue('level1', 'activeTot'), tip: '直接通过你的邀请码注册的用户' },
				{ key: 'level2', label: '推荐粉丝', color: '#67c23a', total: this.getLevelValue('level2', 'tot'), tip: '通过你的直属粉丝邀请码注册的用户' },
				{ key: 'level3', label: '其他粉丝', color: '#e6a23c', total: this.getLevelValue('level3', 'tot'), tip: '通过你的推荐粉丝邀请码注册的用户' }
			]
		}
	},
	mounted() {
		this.fetchOverviewData()
		this.fetchTrendData()
		this.$nextTick(this.initCharts)
		window.addEventListener('resize', this.resizeCharts)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.resizeCharts)
		if (this.pieChart) this.pieChart.dispose()
		if (this.barChart) this.barChart.dispose()
	},
	methods: {
		formatNumber(value) {
			const number = Number(value || 0)
			return Number.isFinite(number) ? number.toLocaleString('zh-CN') : '0'
		},
		getLevelValue(level, key) {
			return (this.overviewData[level] && this.overviewData[level][key]) || 0
		},
		async fetchOverviewData() {
			this.loading = true
			try {
				const response = await getTeamFansIndex({})
				if (response && response.code === 1) {
					this.overviewData = { ...this.overviewData, ...response.data }
					this.$nextTick(this.updatePieChart)
				} else if (!(response && response.isAuthError)) {
					this.$message.error((response && response.msg) || '获取粉丝数据失败')
				}
			} catch (error) {
				if (!(error && error.isAuthError)) console.error('获取团队粉丝概览数据失败:', error)
			} finally {
				this.loading = false
			}
		},
		async fetchTrendData() {
			this.trendLoading = true
			try {
				const response = await getFansByTime({ time: this.trendTime })
				if (response && response.code === 1) {
					this.trendData = response.data || {}
					this.$nextTick(this.updateBarChart)
				}
			} catch (error) {
				if (!(error && error.isAuthError)) console.error('获取趋势数据失败:', error)
			} finally {
				this.trendLoading = false
			}
		},
		initCharts() {
			if (this.$refs.levelPieChart) this.pieChart = echarts.init(this.$refs.levelPieChart)
			if (this.$refs.trendBarChart) this.barChart = echarts.init(this.$refs.trendBarChart)
			this.updatePieChart()
			this.updateBarChart()
		},
		updatePieChart() {
			if (!this.pieChart) return
			this.pieChart.setOption({
				tooltip: { trigger: 'item', formatter: '{b}<br/>{c} 人（{d}%）' },
				legend: { bottom: 0, icon: 'circle', itemWidth: 8, itemHeight: 8, textStyle: { color: '#606266' } },
				series: [{
					type: 'pie', radius: ['48%', '70%'], center: ['50%', '43%'], avoidLabelOverlap: true,
					label: { show: false }, emphasis: { label: { show: true, fontSize: 16, fontWeight: 'bold' } },
					data: this.levelItems.map(item => ({ value: item.total, name: item.label, itemStyle: { color: item.color } }))
				}]
			})
		},
		updateBarChart() {
			if (!this.barChart) return
			this.barChart.setOption({
				tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
				grid: { left: 8, right: 12, top: 24, bottom: 6, containLabel: true },
				xAxis: { type: 'category', data: ['粉丝总数', '有效粉丝', '活跃粉丝'], axisTick: { show: false }, axisLine: { lineStyle: { color: '#dcdfe6' } }, axisLabel: { color: '#606266' } },
				yAxis: { type: 'value', minInterval: 1, splitLine: { lineStyle: { color: '#f0f2f5' } }, axisLine: { show: false }, axisTick: { show: false }, axisLabel: { color: '#909399' } },
				series: [{ type: 'bar', barMaxWidth: 44, data: [
					{ value: this.trendData.fansTot || 0, itemStyle: { color: '#409eff', borderRadius: [5, 5, 0, 0] } },
					{ value: this.trendData.effectiveTot || 0, itemStyle: { color: '#67c23a', borderRadius: [5, 5, 0, 0] } },
					{ value: this.trendData.activeTot || 0, itemStyle: { color: '#e6a23c', borderRadius: [5, 5, 0, 0] } }
				]}]
			})
		},
		resizeCharts() {
			if (this.pieChart) this.pieChart.resize()
			if (this.barChart) this.barChart.resize()
		},
		handleGetShareLink() {
			if (this.shareUrl) {
				this.$refs.shareLinkDialog.open(this.shareUrl)
				return
			}
			this.loading = true
			getAppShareXcode({ uid: this.userInfo.id }).then(res => {
				if (!res || res.code !== 1 || !res.data || !res.data.xcode) throw new Error((res && res.msg) || '获取分享码失败')
				return getAppShareInfo({ xcode: res.data.xcode, user_id: this.userInfo.id, goods_share_header: 2, invite_method: 2, is_wechat: 0 })
			}).then(res => {
				if (!res || res.code !== 1 || !res.data) throw new Error((res && res.msg) || '获取分享信息失败')
				this.shareUrl = res.data.short_url || ''
				if (!this.shareUrl) this.$message.warning('未获取到分享链接')
				this.$refs.shareLinkDialog.open(this.shareUrl)
			}).catch(error => {
				if (!(error && error.isAuthError)) this.$message.error(error.message || '获取分享链接失败，请重试')
			}).finally(() => { this.loading = false })
		}
	}
}
</script>

<style lang="scss" scoped>
.fans-summary-card, .chart-card { border: 1px solid #ebeef5; border-radius: 10px; }
.fans-summary-card { margin-bottom: 16px; ::v-deep .el-card__body { padding: 20px 24px 22px; } }
.card-title-row {
	display: flex; align-items: center; justify-content: space-between; margin-bottom: 20px;
	h3 { display: inline-block; margin: 0 10px 0 0; color: #303133; font-size: 17px; }
	span { color: #a1a6b0; font-size: 13px; }
	&.compact { margin-bottom: 8px; }
}
.fans-metrics { display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); }
.fan-metric {
	position: relative; min-width: 0; padding: 2px 24px; border-right: 1px solid #ebeef5;
	&:first-child { padding-left: 2px; } &:last-child { padding-right: 2px; border-right: 0; }
	.metric-label { margin-bottom: 8px; color: #909399; font-size: 13px; }
	.metric-value { color: #303133; font-family: 'customFont'; font-size: 25px; font-weight: 700; }
	&.is-today .metric-value { color: #409eff; } &.is-seven .metric-value { color: #67c23a; } &.is-thirty .metric-value { color: #e6a23c; }
}
.help-icon { margin-left: 3px; color: #c0c4cc; font-size: 13px; cursor: help; }
.charts-grid { display: grid; grid-template-columns: minmax(420px, 0.9fr) minmax(520px, 1.35fr); gap: 16px; }
.chart-card { min-width: 0; ::v-deep .el-card__body { padding: 20px 24px; } }
.distribution-content { display: flex; align-items: center; min-height: 292px; }
.pie-chart { flex: 1 1 55%; min-width: 230px; height: 280px; }
.level-list { flex: 1 1 45%; min-width: 180px; }
.level-item {
	padding: 13px 0; border-bottom: 1px solid #f0f2f5;
	&:last-child { border-bottom: 0; }
	.level-name { margin-bottom: 5px; color: #606266; font-size: 13px; }
	.level-name > i:first-child { display: inline-block; width: 8px; height: 8px; margin-right: 7px; border-radius: 50%; }
	strong { color: #303133; font-size: 21px; }
	.level-extra { display: flex; gap: 12px; margin-top: 6px; color: #909399; font-size: 12px; }
}
.bar-chart { width: 100%; height: 292px; }
@media (max-width: 1200px) {
	.charts-grid { grid-template-columns: 1fr; }
	.distribution-content { min-height: 270px; }
}
@media (max-width: 768px) {
	.fans-metrics { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 20px; }
	.fan-metric { padding: 2px 12px; border-right: 0; }
	.fan-metric:nth-child(odd) { padding-left: 2px; border-right: 1px solid #ebeef5; }
	.charts-grid { grid-template-columns: minmax(0, 1fr); }
	.card-title-row.compact { align-items: flex-start; flex-direction: column; gap: 12px; }
	.distribution-content { align-items: stretch; flex-direction: column; }
	.pie-chart { width: 100%; min-width: 0; }
	.level-list { width: 100%; }
}
</style>
